import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  Upload,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  uploadFileToStorage,
  MAX_UPLOAD_BYTES,
  FILE_TOO_LARGE_MESSAGE,
} from "@/lib/resumableUpload";

export type AffiliationMode = "read" | "sign" | "scan" | "upload" | "team_confirm";

interface JourneyAffiliationStepProps {
  stepId: string;
  userId: string;
  mode: AffiliationMode;
  documentUrl?: string | null;
  documentLabel?: string;
  documentType?: string;
  isCompleted: boolean;
  actionData?: Record<string, any>;
  onComplete: () => void;
}

const ALLOWED = ["pdf", "jpg", "jpeg", "png", "heic"];

export function JourneyAffiliationStep({
  stepId,
  userId,
  mode,
  documentUrl,
  documentLabel = "Affiliation Agreement",
  documentType = "affiliation_agreement",
  isCompleted,
  actionData,
  onComplete,
}: JourneyAffiliationStepProps) {
  const { toast } = useToast();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [percent, setPercent] = useState(0);

  // Mark this step done and open the next one, the same way the other
  // journey steps do it.
  const finish = useCallback(
    async (data: Record<string, unknown>) => {
      const { error } = await supabase
        .from("user_journey_step_progress")
        .upsert(
          {
            user_id: userId,
            step_id: stepId,
            status: "completed",
            completed_at: new Date().toISOString(),
            verified_completion: true,
            action_data: data as any,
          } as any,
          { onConflict: "user_id,step_id" },
        );
      if (error) throw error;

      const { data: current } = await supabase
        .from("journey_steps")
        .select("step_number")
        .eq("id", stepId)
        .maybeSingle();

      if (current) {
        const { data: next } = await supabase
          .from("journey_steps")
          .select("id")
          .eq("step_number", current.step_number + 1)
          .eq("is_active", true)
          .maybeSingle();

        if (next) {
          await supabase.from("user_journey_step_progress").upsert(
            {
              user_id: userId,
              step_id: next.id,
              status: "active",
              started_at: new Date().toISOString(),
            } as any,
            { onConflict: "user_id,step_id" },
          );
        }
      }
      onComplete();
    },
    [onComplete, stepId, userId],
  );

  const confirm = async (data: Record<string, unknown>) => {
    setBusy(true);
    try {
      await finish(data);
    } catch (error: any) {
      toast({
        title: "That did not save",
        description: error?.message || "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    const ext = chosen.name.split(".").pop()?.toLowerCase();
    if (!ext || !ALLOWED.includes(ext)) {
      toast({
        title: "We cannot read that file",
        description: "Send a PDF or a photo file such as JPG, PNG, or HEIC.",
        variant: "destructive",
      });
      return;
    }
    if (chosen.size > MAX_UPLOAD_BYTES) {
      toast({
        title: "That file is too big",
        description: FILE_TOO_LARGE_MESSAGE,
        variant: "destructive",
      });
      return;
    }
    setFile(chosen);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setPercent(0);
    try {
      const ext = file.name.split(".").pop();
      const path = `${userId}/${documentType}_${Date.now()}.${ext}`;

      await uploadFileToStorage({
        bucket: "journey-documents",
        path,
        file,
        onProgress: setPercent,
      });

      const { error: docError } = await supabase.from("user_journey_documents").insert({
        user_id: userId,
        document_type: documentType,
        document_name: file.name,
        file_url: path,
        file_path: path,
        file_size: file.size,
        review_status: "awaiting_team_review",
      } as any);
      if (docError) throw docError;

      await finish({
        document_path: path,
        document_name: file.name,
        review_status: "awaiting_team_review",
      });

      toast({
        title: "We have your signed agreement",
        description: "Our team will look it over and add it to your record.",
      });
    } catch (error: any) {
      toast({
        title: "Your file did not save",
        description:
          error?.message || "Check your connection and try again. Nothing was lost.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      setPercent(0);
    }
  };

  // The team confirmed step never has a member button.
  if (mode === "team_confirm") {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          This last part is ours. Once we have looked at your signed agreement, we file it
          with your record and mark this step done for you.
        </p>
        {isCompleted ? (
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            Your agreement is filed with your record.
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            With our team now. Nothing for you to do here.
          </div>
        )}
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
        <CheckCircle2 className="h-4 w-4" />
        {mode === "upload" ? "Your signed copy is with us." : "Done. Thank you."}
      </div>
    );
  }

  if (mode === "read") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The Affiliation Agreement says how your association and Wealthy Steward walk
          together. Read all of it before you sign. Take your time.
        </p>
        {documentUrl ? (
          <Button variant="outline" asChild>
            <a href={documentUrl} target="_blank" rel="noopener noreferrer">
              <FileText className="mr-2 h-4 w-4" />
              Open the {documentLabel}
              <ExternalLink className="ml-2 h-3.5 w-3.5" />
            </a>
          </Button>
        ) : (
          <div className="rounded-lg border border-dashed p-3 text-sm text-muted-foreground">
            Your copy of the agreement came with your delivered documents. A link will sit
            here as soon as we post it in the portal.
          </div>
        )}
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => setChecked(value === true)}
            className="mt-0.5"
          />
          <span>I have read the whole Affiliation Agreement.</span>
        </label>
        <Button
          disabled={!checked || busy}
          onClick={() => void confirm({ read_confirmed: true })}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I have read it
        </Button>
      </div>
    );
  }

  if (mode === "sign") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Sign with blue ink. Blue ink shows at a glance which copy is the one you signed
          by hand. Sign and date every place the agreement asks for a signature, and print
          your name where it asks for that too.
        </p>
        <p className="text-sm text-muted-foreground">
          This is a steward's choice. You sign because you mean it, not because a screen
          told you to.
        </p>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => setChecked(value === true)}
            className="mt-0.5"
          />
          <span>I have signed the agreement in blue ink.</span>
        </label>
        <Button
          disabled={!checked || busy}
          onClick={() => void confirm({ signed_confirmed: true, ink: "blue" })}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          I have signed it
        </Button>
      </div>
    );
  }

  if (mode === "scan") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Now make a copy we can read. A scanner is great. Your phone camera works just as
          well.
        </p>
        <ul className="ml-5 list-disc space-y-1 text-sm text-muted-foreground">
          <li>Every page, in order, including any page you only initialed.</li>
          <li>Good light, no shadow across the page.</li>
          <li>All four corners in the picture, and the writing easy to read.</li>
          <li>One PDF is best. Separate photos are fine too.</li>
        </ul>
        <label className="flex items-start gap-3 text-sm">
          <Checkbox
            checked={checked}
            onCheckedChange={(value) => setChecked(value === true)}
            className="mt-0.5"
          />
          <span>I have a clear copy of every page.</span>
        </label>
        <Button
          disabled={!checked || busy}
          onClick={() => void confirm({ scan_confirmed: true })}
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          My copy is ready
        </Button>
      </div>
    );
  }

  // mode === "upload"
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Send us the copy you just made. We keep it with your record and our team looks it
        over.
      </p>
      <input
        id={`affiliation-file-${stepId}`}
        type="file"
        className="sr-only"
        accept=".pdf,.jpg,.jpeg,.png,.heic"
        onChange={pickFile}
        disabled={busy}
      />
      <label
        htmlFor={`affiliation-file-${stepId}`}
        className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-dashed p-4 text-sm"
      >
        <Upload className="h-4 w-4 text-muted-foreground" />
        {file ? file.name : "Choose your signed copy"}
      </label>
      {busy && percent > 0 && <Progress value={percent} className="h-2" />}
      <Button disabled={!file || busy} onClick={() => void upload()}>
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Send it to us
      </Button>
      {actionData?.document_name && (
        <p className="text-xs text-muted-foreground">Last sent: {actionData.document_name}</p>
      )}
    </div>
  );
}

export default JourneyAffiliationStep;
